import type { ReactNode } from "react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from "react";

import type { PageState } from "../navigation/PageStateProvider";
import { useInternalPageState } from "../navigation/PageStateProvider";
import type { ThirdPartyTrackingAgent } from "./thirdPartyTrackingAgent";
import { ThirdPartyTrackingAgentContext } from "./TrackingProvider";

interface TrackingContextValue {
  pageMapper: (page: string) => string;
  defaultCategory: string;
}
interface CreateTrackingPageProviderProps {
  category: TrackingContextValue["defaultCategory"];
  pageMapper: TrackingContextValue["pageMapper"];
  pageViewName?: string;
}

export const TrackingContext = createContext<TrackingContextValue | null>(null);

type TrackEventArgs =
  | [string, string]
  | [
      {
        action: string;
        label: string;
        page?: string;
        category?: string;
      },
    ];

const parseTrackEventArgs = (args: TrackEventArgs) => {
  if (typeof args[0] === "string") {
    return {
      action: args[0],
      label: args[1] as string,
      page: undefined,
      category: undefined,
    };
  }
  return args[0];
};

const getOpenPage = (pageState: PageState) => {
  if (typeof pageState !== "object" || !Object.keys(pageState).length) {
    return "";
  }

  const openPageState = Object.entries(pageState).find(
    ([, { state }]) => state === "open",
  );
  return openPageState?.[0] ?? "";
};

export const createTrackingPageProvider = ({
  category: defaultCategory,
  pageMapper,
  pageViewName,
}: CreateTrackingPageProviderProps) => {
  return function TrackingPageProvider({ children }: { children: ReactNode }) {
    const thirdPartyTrackingAgent = useContext(ThirdPartyTrackingAgentContext);

    useEffect(() => {
      if (thirdPartyTrackingAgent.trackPage && pageViewName) {
        thirdPartyTrackingAgent.trackPage(pageViewName);
      }
    }, [thirdPartyTrackingAgent]);

    return (
      <TrackingContext.Provider
        value={{
          pageMapper,
          defaultCategory,
        }}
      >
        {children}
      </TrackingContext.Provider>
    );
  };
};

export const useTracking = (): ThirdPartyTrackingAgent => {
  const trackingPageContext = useContext(TrackingContext);
  const thirdPartyTrackingAgent = useContext(ThirdPartyTrackingAgentContext);
  const pageState = useInternalPageState() as PageState;
  const { defaultCategory, pageMapper } = trackingPageContext || {};
  const pageFromContext = pageMapper ? pageMapper(getOpenPage(pageState)) : "";

  const trackEvent = useCallback(
    (...args: TrackEventArgs) => {
      const {
        action,
        label,
        page,
        category = defaultCategory,
      } = parseTrackEventArgs(args);
      thirdPartyTrackingAgent.trackEvent({
        action,
        label,
        page: page || pageFromContext,
        category,
      });
    },
    [defaultCategory, pageFromContext, thirdPartyTrackingAgent],
  );

  return {
    trackPage: thirdPartyTrackingAgent.trackPage,
    trackSurvey: thirdPartyTrackingAgent.trackSurvey,
    setCustomDimension: thirdPartyTrackingAgent.setCustomDimension,
    trackFireEvent: thirdPartyTrackingAgent.trackFireEvent,
    trackEvent,
    trackQualtrics: thirdPartyTrackingAgent.trackQualtrics,
    trackWebVitals: thirdPartyTrackingAgent.trackWebVitals,
  };
};
